import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Headphones, Play, Pause, RotateCcw, RotateCw, Loader2, Trash2, Sparkles, Car,
  AlertTriangle, FileText, ChevronDown, ChevronUp, SkipForward,
} from "lucide-react";

const EXAM_BADGE = {
  cphq: "bg-violet-500/15 text-violet-300 border-violet-500/40",
  cphrm: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
  chc: "bg-amber-500/15 text-amber-300 border-amber-500/40",
};
const fmt = (s) => { s = Math.max(0, Math.floor(s || 0)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };
const SPEEDS = [1, 1.25, 1.5];

export function Lectures() {
  const [sugg, setSugg] = useState(null);
  const [lectures, setLectures] = useState([]);
  const [active, setActive] = useState(null);
  const [creating, setCreating] = useState(false);
  const [customTopic, setCustomTopic] = useState("");
  const [customExam, setCustomExam] = useState("");
  const [pickWeek, setPickWeek] = useState("");

  const loadList = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/certprep/lectures`, { withCredentials: true });
      setLectures(data);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    axios.get(`${API}/certprep/lectures/suggestions`, { withCredentials: true }).then(r => setSugg(r.data)).catch(() => {});
    loadList();
  }, [loadList]);

  // Poll while anything is building
  useEffect(() => {
    if (!lectures.some(l => l.status === "building")) return;
    const t = setInterval(loadList, 5000);
    return () => clearInterval(t);
  }, [lectures, loadList]);

  const create = async (body) => {
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/certprep/lectures`, body, { withCredentials: true });
      toast.success("Recording your lecture — about 2 minutes. It'll appear below when ready.");
      setLectures(l => [data, ...l]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start lecture");
    } finally { setCreating(false); }
  };

  const open = async (l) => {
    if (l.status !== "ready") return;
    try {
      const { data } = await axios.get(`${API}/certprep/lectures/${l.lecture_id}`, { withCredentials: true });
      setActive(data);
    } catch { toast.error("Couldn't open lecture"); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete "${l.title}"?`)) return;
    await axios.delete(`${API}/certprep/lectures/${l.lecture_id}`, { withCredentials: true });
    if (active?.lecture_id === l.lecture_id) setActive(null);
    loadList();
  };

  return (
    <div className="space-y-4" data-testid="lectures-tab">
      {active && <LecturePlayer lecture={active} onClose={() => { setActive(null); loadList(); }} />}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base md:text-lg flex items-center gap-2"><Car className="w-4 h-4 text-primary" /> Commute lecture</CardTitle>
          <p className="text-xs text-muted-foreground">Two 15-minute narrated segments — "Core concepts" out, "Exam traps & rapid recall" back. Podcast-quality voice, cached for replay.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {sugg && (
            <div className="space-y-2" data-testid="lecture-suggestions">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">This week · Week {sugg.week.week} · {sugg.week.phase}</div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" disabled={creating} onClick={() => create({ mode: "week", week: sugg.week.week })} data-testid="lecture-week-btn"
                  className="max-w-full h-auto py-1.5 whitespace-normal text-left">
                  <Sparkles className="w-3.5 h-3.5 mr-1 shrink-0" /> Whole week: {sugg.week.title}
                </Button>
                {sugg.week.topics.map((t, i) => (
                  <Button key={i} size="sm" variant="ghost" className="max-w-full h-auto py-1.5 text-xs border border-zinc-800 whitespace-normal text-left" disabled={creating}
                    onClick={() => create({ mode: "topic", topic: t, exam: sugg.week.exams[0] })} data-testid={`lecture-topic-btn-${i}`}>
                    {t}
                  </Button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button size="sm" variant="outline" disabled={creating || !sugg.weak_topics.length} onClick={() => create({ mode: "weak" })} data-testid="lecture-weak-btn"
                  className="border-rose-500/40 text-rose-200">
                  <AlertTriangle className="w-3.5 h-3.5 mr-1" /> My weak spots {sugg.weak_topics.length ? `(${sugg.weak_topics.length})` : "— none yet"}
                </Button>
                <select value={pickWeek} onChange={(e) => { setPickWeek(e.target.value); if (e.target.value) create({ mode: "week", week: Number(e.target.value) }); setPickWeek(""); }}
                  className="h-8 max-w-full rounded-md border border-zinc-700 bg-transparent px-2 text-xs" data-testid="lecture-week-select" disabled={creating}>
                  <option value="">Any week…</option>
                  {sugg.all_weeks.map(w => <option key={w.week} value={w.week}>Week {w.week} · {w.title}</option>)}
                </select>
              </div>
            </div>
          )}

          <form className="flex flex-wrap gap-2 items-center" onSubmit={(e) => { e.preventDefault(); if (customTopic.trim()) { create({ mode: "topic", topic: customTopic.trim(), exam: customExam || undefined }); setCustomTopic(""); } }}>
            <input value={customTopic} onChange={(e) => setCustomTopic(e.target.value)} placeholder="Custom topic… e.g. Stark Law exceptions"
              className="flex-1 min-w-[200px] h-9 rounded-md border border-zinc-700 bg-transparent px-3 text-sm" data-testid="lecture-custom-topic" />
            <select value={customExam} onChange={(e) => setCustomExam(e.target.value)} className="h-9 rounded-md border border-zinc-700 bg-transparent px-2 text-sm" data-testid="lecture-custom-exam">
              <option value="">All exams</option><option value="cphq">CPHQ</option><option value="cphrm">CPHRM</option><option value="chc">CHC</option>
            </select>
            <Button type="submit" size="sm" disabled={creating || !customTopic.trim()} data-testid="lecture-custom-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Headphones className="w-4 h-4 mr-1" />} Record
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Headphones className="w-4 h-4" /> Your lectures</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {lectures.length === 0 && <div className="text-sm text-muted-foreground" data-testid="lectures-empty">Nothing recorded yet. Pick a topic above — it'll be ready before you reach the car.</div>}
          {lectures.map(l => (
            <div key={l.lecture_id} className="flex items-center gap-3 p-3 rounded-lg border border-zinc-800 hover:border-zinc-700" data-testid={`lecture-row-${l.lecture_id}`}>
              <button className="flex-1 text-left min-w-0" onClick={() => open(l)} disabled={l.status !== "ready"}>
                <div className="text-sm font-medium truncate">{l.title}</div>
                <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
                  {l.exams?.map(e => <Badge key={e} className={`text-[9px] ${EXAM_BADGE[e]}`}>{e.toUpperCase()}</Badge>)}
                  {l.status === "ready" && <span>~{Math.round((l.total_est_sec || 0) / 60)} min · 2 segments</span>}
                  {l.status === "ready" && l.progress?.position_sec > 5 && <span className="text-primary">· resume seg {l.progress.segment_index + 1} @ {fmt(l.progress.position_sec)}</span>}
                  {l.status === "building" && <span className="flex items-center gap-1 text-amber-300"><Loader2 className="w-3 h-3 animate-spin" /> recording…</span>}
                  {l.status === "failed" && <span className="text-rose-300">failed — {l.error}</span>}
                </div>
              </button>
              {l.status === "ready" && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => open(l)} data-testid={`lecture-play-${l.lecture_id}`}><Play className="w-4 h-4" /></Button>}
              <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-rose-300" onClick={() => remove(l)} data-testid={`lecture-delete-${l.lecture_id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

/* --------------------------------- Player --------------------------------- */
function LecturePlayer({ lecture, onClose }) {
  const audioRef = useRef(null);
  const [seg, setSeg] = useState(lecture.progress?.segment_index || 0);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [speed, setSpeed] = useState(() => Number(localStorage.getItem("niko_lecture_speed")) || 1);
  const [showScript, setShowScript] = useState(false);
  const lastSave = useRef(0);
  const resumeOnce = useRef(true);

  const src = `${API}/certprep/lectures/${lecture.lecture_id}/audio/${seg}`;
  const segment = lecture.segments[seg];

  const saveProgress = useCallback((force = false) => {
    const a = audioRef.current;
    if (!a) return;
    const now = Date.now();
    if (!force && now - lastSave.current < 5000) return;
    lastSave.current = now;
    axios.put(`${API}/certprep/lectures/${lecture.lecture_id}/progress`, { segment_index: seg, position_sec: a.currentTime }, { withCredentials: true }).catch(() => {});
  }, [lecture.lecture_id, seg]);

  // Lock-screen / CarPlay controls
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const ms = navigator.mediaSession;
    ms.metadata = new window.MediaMetadata({ title: `${segment?.title} — ${lecture.title}`, artist: "Niko Cert Prep", album: lecture.exams?.map(e => e.toUpperCase()).join(" · ") });
    const a = () => audioRef.current;
    ms.setActionHandler("play", () => a()?.play());
    ms.setActionHandler("pause", () => a()?.pause());
    ms.setActionHandler("seekbackward", () => { if (a()) a().currentTime = Math.max(0, a().currentTime - 30); });
    ms.setActionHandler("seekforward", () => { if (a()) a().currentTime = Math.min(a().duration || 0, a().currentTime + 30); });
    ms.setActionHandler("nexttrack", () => setSeg(s => Math.min(1, s + 1)));
    ms.setActionHandler("previoustrack", () => setSeg(s => Math.max(0, s - 1)));
    return () => ["play", "pause", "seekbackward", "seekforward", "nexttrack", "previoustrack"].forEach(k => { try { ms.setActionHandler(k, null); } catch { /* noop */ } });
  }, [segment?.title, lecture.title, lecture.exams]);

  useEffect(() => { if (audioRef.current) audioRef.current.playbackRate = speed; localStorage.setItem("niko_lecture_speed", String(speed)); }, [speed, seg]);

  const toggle = () => { const a = audioRef.current; if (!a) return; playing ? a.pause() : a.play().catch(() => toast.error("Tap play again — iPhone needs a direct tap to start audio")); };
  const skip = (d) => { const a = audioRef.current; if (a) a.currentTime = Math.max(0, Math.min(a.duration || 0, a.currentTime + d)); };

  return (
    <Card className="border-primary/50 shadow-lg shadow-primary/10 sticky top-2 z-10 bg-zinc-950" data-testid="lecture-player">
      <CardContent className="pt-4 pb-4 space-y-3">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-xs text-muted-foreground">Now playing</div>
            <div className="font-semibold leading-tight truncate" data-testid="lecture-player-title">{lecture.title}</div>
          </div>
          <Button size="sm" variant="ghost" onClick={() => { saveProgress(true); onClose(); }} data-testid="lecture-player-close">Close</Button>
        </div>

        <div className="flex gap-1">
          {lecture.segments.map(s => (
            <button key={s.index} onClick={() => { saveProgress(true); setSeg(s.index); setCur(0); }}
              className={`flex-1 text-left rounded-md border px-2 py-1.5 text-xs ${seg === s.index ? "border-primary bg-primary/10" : "border-zinc-800 text-muted-foreground"}`}
              data-testid={`lecture-seg-${s.index}`}>
              <div className="font-medium">{s.index + 1}. {s.title}</div>
              <div className="text-[10px] opacity-70">~{Math.round(s.est_duration_sec / 60)} min</div>
            </button>
          ))}
        </div>

        <audio
          ref={audioRef}
          key={src}
          src={src}
          preload="metadata"
          playsInline
          onLoadedMetadata={(e) => {
            setDur(e.target.duration);
            e.target.playbackRate = speed;
            if (resumeOnce.current && lecture.progress?.segment_index === seg && lecture.progress?.position_sec > 3) {
              e.target.currentTime = lecture.progress.position_sec;
              resumeOnce.current = false;
            }
          }}
          onTimeUpdate={(e) => { setCur(e.target.currentTime); saveProgress(); }}
          onPlay={() => setPlaying(true)}
          onPause={() => { setPlaying(false); saveProgress(true); }}
          onEnded={() => { setPlaying(false); if (seg < lecture.segments.length - 1) { setSeg(seg + 1); setCur(0); setTimeout(() => audioRef.current?.play().catch(() => {}), 300); } else { toast.success("Lecture complete — nice drive."); } }}
          onError={() => toast.error("Audio failed to load — try again in a moment")}
          data-testid="lecture-audio"
        />

        <input type="range" min={0} max={dur || 0} step={1} value={Math.min(cur, dur || 0)}
          onChange={(e) => { if (audioRef.current) audioRef.current.currentTime = Number(e.target.value); }}
          className="w-full accent-[hsl(var(--primary))]" data-testid="lecture-seek" />
        <div className="flex items-center justify-between text-[11px] text-muted-foreground -mt-1">
          <span data-testid="lecture-time">{fmt(cur)}</span><span>-{fmt((dur || 0) - cur)}</span>
        </div>

        <div className="flex items-center justify-center gap-3">
          <Button size="icon" variant="outline" className="h-11 w-11 rounded-full" onClick={() => skip(-30)} data-testid="lecture-back30"><RotateCcw className="w-5 h-5" /></Button>
          <Button size="icon" className="h-16 w-16 rounded-full" onClick={toggle} data-testid="lecture-play-pause">
            {playing ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 ml-0.5" />}
          </Button>
          <Button size="icon" variant="outline" className="h-11 w-11 rounded-full" onClick={() => skip(30)} data-testid="lecture-fwd30"><RotateCw className="w-5 h-5" /></Button>
          <Button size="sm" variant="ghost" className="h-11 w-14 rounded-full font-mono" onClick={() => setSpeed(SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length])} data-testid="lecture-speed">
            {speed}×
          </Button>
          {seg < lecture.segments.length - 1 && (
            <Button size="icon" variant="ghost" className="h-11 w-11 rounded-full" onClick={() => { saveProgress(true); setSeg(seg + 1); setCur(0); }} data-testid="lecture-next-seg"><SkipForward className="w-5 h-5" /></Button>
          )}
        </div>

        <button className="w-full flex items-center justify-center gap-1 text-xs text-muted-foreground" onClick={() => setShowScript(s => !s)} data-testid="lecture-transcript-toggle">
          <FileText className="w-3.5 h-3.5" /> Transcript {showScript ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        {showScript && (
          <div className="max-h-64 overflow-y-auto text-sm leading-relaxed whitespace-pre-wrap text-foreground/90 border-t border-zinc-800 pt-3" data-testid="lecture-transcript">
            {segment?.script}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
